import startWordTable from '../../../../shared/pkg/prepacks/startWords.json'

const SPORT_WORD_SEPARATOR_PATTERN = /[_\s-]+/
const HYPHEN_OR_SPACE_PATTERN = /[- ]/g

// Sport ids whose label is an acronym, not a capitalized word.
const SPORT_ACRONYMS = { mma: 'MMA' }

/**
 * Humanizes a canonical sport identifier for display (`american_football` -> `American Football`, `mma` -> `MMA`).
 */
export function formatSportLabel(sport) {
  return String(sport || '')
    .split(SPORT_WORD_SEPARATOR_PATTERN)
    .filter(Boolean)
    .map((word) => SPORT_ACRONYMS[word.toLowerCase()] || word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')
}

export const DEFAULT_START_WORD = 'kickoff'

/**
 * What a schedule calls the start of a game, by canonical sport id (the
 * prototype's `SPORTS[].word`), and the sport spellings and league names
 * that resolve to those sports. One table, startWords.json, which Go's
 * apps/shared/pkg/prepacks/startword.go embeds, so Studio and the server
 * name the same start.
 */
export const START_WORDS = Object.freeze({ ...startWordTable.words })
const START_WORD_ALIASES = Object.freeze({ ...startWordTable.aliases })

// A sport or league as the table keys it, as Go's startToken writes it:
// "Ice Hockey" is ice_hockey, "NBA" is nba.
const startToken = (value) => String(value ?? '').trim().toLowerCase().replace(HYPHEN_OR_SPACE_PATTERN, '_')

// The canonical sport a sport or league names, else the token.
function startSport(value) {
  const token = startToken(value)
  return Object.hasOwn(START_WORD_ALIASES, token) ? START_WORD_ALIASES[token] : token
}

const tokens = (values) => (Array.isArray(values) ? values : []).map(startSport).filter(Boolean)

/**
 * The start word of a schedule over these sports and leagues (`NBA`,
 * `basketball`): the one word they share ("tip-off" for basketball,
 * "kickoff" for football and soccer), else "kickoff": no sport, sports with
 * different words, or a sport outside the table. A league whose sport is not
 * in the table says nothing. `pinned` (a pack type's sports) counts only
 * when sports and leagues name none. Go's StartWord / RecipeStartWord apply
 * the same rule.
 */
export function startWord(sports = [], leagues = [], pinned = []) {
  let scope = [...tokens(sports), ...tokens(leagues).filter((sport) => Object.hasOwn(START_WORDS, sport))]
  if (scope.length === 0) scope = tokens(pinned)
  const words = new Set(scope.map((sport) => (Object.hasOwn(START_WORDS, sport) ? START_WORDS[sport] : DEFAULT_START_WORD)))
  return words.size === 1 ? [...words][0] : DEFAULT_START_WORD
}
